import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

import Logo from "../components/Logo";
import SwipeCard from "../components/SwipeCard";
import DetailsModal from "../components/DetailsModal";
import EmptyDeck from "../components/EmptyDeck";
import AgeGate, { hasVerifiedAge } from "../components/AgeGate";
import CategoryBar from "../components/CategoryBar";
import PushOptInBanner from "../components/PushOptInBanner";
import InstallAppButton from "../components/InstallAppButton";
import { api } from "../lib/api";
import { trackVisitOnce } from "../lib/tracking";

const ADMIN_CLICKS_REQUIRED = 7;
const ADMIN_CLICK_WINDOW_MS = 4000;

export default function SwipePage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cursor, setCursor] = useState(0);
    const [modalProduct, setModalProduct] = useState(null);
    const [ageOk, setAgeOk] = useState(() => hasVerifiedAge());
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const secretClicks = useRef(0);
    const lastClickAt = useRef(0);
    const navigate = useNavigate();

    const load = async (categoryId = activeCategory) => {
        setLoading(true);
        try {
            const params = categoryId ? { category_id: categoryId } : {};
            const { data } = await api.get("/products", { params });
            setProducts(data);
            setCursor(0);
        } catch (e) {
            toast.error("Could not load products");
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const { data } = await api.get("/categories");
            setCategories(data);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        if (ageOk) {
            trackVisitOnce();
        }
    }, [ageOk]);

    useEffect(() => {
        load(activeCategory);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategory]);

    const remaining = useMemo(() => products.slice(cursor), [products, cursor]);
    const topTwo = remaining.slice(0, 2);

    const openBuyLink = (product) => {
        if (product.buy_link) {
            window.open(product.buy_link, "_blank", "noopener,noreferrer");
        } else {
            toast.success(`Added "${product.title}" to your wishlist`);
        }
    };

    const handleSwipe = async (product, direction) => {
        setCursor((c) => c + 1);
        try {
            await api.post("/swipes", {
                product_id: product.id,
                direction,
            });
        } catch {
            /* non-blocking */
        }
        if (direction === "buy") {
            openBuyLink(product);
        }
    };

    const triggerSwipe = (direction) => {
        const top = remaining[0];
        if (!top) return;
        handleSwipe(top, direction);
    };

    const handleSecretTap = () => {
        const now = Date.now();
        const within = now - lastClickAt.current < ADMIN_CLICK_WINDOW_MS;
        const next = within ? secretClicks.current + 1 : 1;
        lastClickAt.current = now;
        secretClicks.current = next;
        if (next >= ADMIN_CLICKS_REQUIRED) {
            secretClicks.current = 0;
            navigate("/admin");
        }
    };

    return (
        <div className="relative flex flex-col w-full max-w-md mx-auto bg-[#050505] text-white" style={{ minHeight: "100svh", height: "100dvh" }}>
            {/* Header */}
            <header
                data-testid="app-header"
                className="flex justify-between items-center px-5 pb-3 z-50"
                style={{
                    paddingTop:
                        "max(1.5rem, calc(env(safe-area-inset-top) + 0.5rem))",
                }}
            >
                <Logo />
                <div className="flex items-center gap-2">
                    <InstallAppButton />
                    <button
                        type="button"
                        onClick={handleSecretTap}
                        data-testid="admin-secret-tap"
                        aria-label=" "
                        tabIndex={-1}
                        className="w-10 h-10 rounded-full bg-transparent border-0 outline-none focus:outline-none cursor-default opacity-0"
                    />
                </div>
            </header>

            {/* Categories */}
            <CategoryBar
                categories={categories}
                activeId={activeCategory}
                onSelect={setActiveCategory}
            />

            {/* Card stack */}
            <div
                data-testid="card-stack"
                className="flex-1 relative flex items-center justify-center px-5"
            >
                <div className="relative w-full max-w-sm aspect-[3/4] sm:aspect-[3/4]">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center text-white/50">
                            Loading drop…
                        </div>
                    )}
                    {!loading && remaining.length === 0 && (
                        <EmptyDeck onReset={() => load(activeCategory)} />
                    )}
                    <AnimatePresence>
                        {topTwo
                            .slice()
                            .reverse()
                            .map((p, idx) => {
                                const i = topTwo.length - 1 - idx;
                                return (
                                    <SwipeCard
                                        key={p.id}
                                        product={p}
                                        index={i}
                                        isTop={i === 0}
                                        onSwipe={handleSwipe}
                                        onInfo={setModalProduct}
                                    />
                                );
                            })}
                    </AnimatePresence>
                </div>
            </div>

            {/* Controls */}
            <div
                className="px-6 pt-2 z-40 flex-shrink-0"
                style={{
                    paddingBottom:
                        "max(6rem, calc(env(safe-area-inset-bottom) + 4rem))",
                }}
            >
                <div className="flex justify-center items-center gap-8">
                    <button
                        data-testid="dont-buy-btn"
                        onClick={() => triggerSwipe("dont_buy")}
                        disabled={remaining.length === 0}
                        className="group flex flex-col items-center gap-1 disabled:opacity-40"
                    >
                        <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#1A1A1A] border-2 border-white/10 group-hover:border-white/40 transition-all shadow-lg">
                            <X
                                size={28}
                                className="text-white/70 group-hover:text-white"
                            />
                        </span>
                        <span className="text-[11px] uppercase tracking-widest text-white/60 group-hover:text-white">
                            Oh no
                        </span>
                    </button>

                    <button
                        data-testid="buy-btn"
                        onClick={() => triggerSwipe("buy")}
                        disabled={remaining.length === 0}
                        className="group flex flex-col items-center gap-1 disabled:opacity-40"
                    >
                        <motion.span
                            whileTap={{ scale: 0.92 }}
                            className="flex items-center justify-center w-20 h-20 rounded-full kw-orange-bg border-4 border-black text-black font-bold shadow-[0_0_22px_rgba(255,90,0,0.45)]"
                        >
                            <ShoppingBag size={30} />
                        </motion.span>
                        <span className="text-[11px] uppercase tracking-widest kw-orange">
                            Oh yes
                        </span>
                    </button>
                </div>
                <p className="text-center text-[11px] uppercase tracking-[0.2em] text-white/30 mt-3">
                    Swipe left or right · Tap card for details
                </p>
            </div>

            <DetailsModal
                product={modalProduct}
                open={!!modalProduct}
                onClose={() => setModalProduct(null)}
                onBuy={(p) => {
                    setModalProduct(null);
                    openBuyLink(p);
                }}
            />

            {!ageOk && <AgeGate onAccept={() => setAgeOk(true)} />}
            {ageOk && <PushOptInBanner />}
        </div>
    );
}
